import React, { useState } from "react";
import { TrendingUp, MessageCircle, ArrowUpCircle } from "lucide-react";

const TrendCard = ({ trend, index, onSave, isSaved, isPro, onUpgradeClick, cardShape }) => {
  const [imageError, setImageError] = useState(false);

  // Extract backend fields
  const name = trend.name;
  const tagline = trend.description;
  const image = trend.image || trend.thumbnail;
  const category = trend.category || (Array.isArray(trend.tags) ? trend.tags[0] : "General");
  const trendScore = trend.score || 0;
  const votes = trend.score || 0;
  const comments = 0; // backend does not provide comments yet

  // Category-based gradient mapping
  const getCategoryGradient = (cat) => {
    const gradients = {
      "Enterprise AI": "from-purple-500 to-indigo-500",
      "Low-Code/VBA Automation": "from-blue-500 to-cyan-500",
      "Workplace Efficiency": "from-amber-500 to-yellow-500",
      "Business Intelligence": "from-emerald-500 to-green-500",
      "Sales & Marketing": "from-rose-500 to-pink-500",
      "Cybersecurity": "from-slate-600 to-gray-700",
    };
    return gradients[cat] || "from-gray-500 to-gray-600";
  };

  return (
    <div
      className="group relative bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 break-inside-avoid mb-6"
    >
      {/* Image Section */}
      <div className="relative w-full aspect-[4/3] overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
        {image && !imageError ? (
          <img
            src={image}
            alt={name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-100 via-blue-50 to-indigo-100">
            <TrendingUp className="w-16 h-16 text-gray-300" />
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Category Badge */}
        <div className="absolute top-3 left-3">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white shadow-lg bg-gradient-to-r ${getCategoryGradient(
              category
            )} backdrop-blur-sm`}
          >
            {category}
          </span>
        </div>

        {/* Trend Score Badge */}
        {trendScore > 0 && (
          <div className="absolute top-3 right-3">
            <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur-sm shadow-md">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
              <span className="text-xs font-bold text-gray-900">{trendScore}</span>
            </div>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-5">
        {/* Name */}
        <h3 className="font-bold text-lg text-gray-900 mb-2 leading-tight group-hover:text-purple-600 transition-colors duration-200">
          {name}
        </h3>

        {/* Tagline */}
        {tagline && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">
            {tagline}
          </p>
        )}

        {/* Metrics Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          {/* Votes */}
          <div
            className="flex items-center gap-1.5 text-gray-500 hover:text-purple-600 transition-colors cursor-pointer"
            onClick={() => onSave(trend.trend_id)}
          >
            <ArrowUpCircle className="w-4 h-4" />
            <span className="text-sm font-semibold">{votes}</span>
          </div>

          {/* Comments */}
          <div className="flex items-center gap-1.5 text-gray-500 hover:text-blue-600 transition-colors cursor-pointer">
            <MessageCircle className="w-4 h-4" />
            <span className="text-sm font-semibold">{comments}</span>
          </div>

          {/* Visual Separator */}
          <div className="flex-1 mx-3 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

          {/* Trend Indicator */}
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-medium text-emerald-700">Trending</span>
          </div>
        </div>
      </div>

      {/* Hover Accent Border */}
      <div className="absolute inset-0 rounded-2xl border-2 border-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
    </div>
  );
};

export default TrendCard;
